import React from 'react';
import { motion, MotionValue, useReducedMotion, useTransform } from 'framer-motion';
import type { VentureSpaceScene } from '../../data/velaris';
type SceneCaptionProps = {
  scene: VentureSpaceScene;
  index: number;
  total: number;
  local: MotionValue<number>; // 0 … 1 through the current environment
  sx: MotionValue<number>; // cursor, -1 … 1
  sy: MotionValue<number>;
};
const ease = [0.22, 1, 0.36, 1] as const;

// Editorial captions laid over the 360° view; layers drift at different depths
export function SceneCaption({
  scene,
  index,
  total,
  local,
  sx,
  sy
}: SceneCaptionProps) {
  const reduce = useReducedMotion();
  const k = reduce ? 0 : 1;
  const timeX = useTransform(sx, [-1, 1], [26 * k, -26 * k]);
  const timeY = useTransform(local, [0, 1], [30 * k, -50 * k]);
  const headX = useTransform(sx, [-1, 1], [12 * k, -12 * k]);
  const headY = useTransform(local, [0, 1], [18 * k, -24 * k]);
  const chipX = useTransform(sx, [-1, 1], [-14 * k, 14 * k]);
  const chipY = useTransform(sy, [-1, 1], [-8 * k, 8 * k]);
  const item = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 28 * k,
      filter: `blur(${10 * k}px)`
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)'
    },
    exit: {
      opacity: 0,
      y: -20 * k,
      filter: `blur(${8 * k}px)`,
      transition: {
        duration: 0.5,
        ease
      }
    },
    transition: {
      duration: 1.1,
      ease,
      delay
    }
  });
  return <div className="pointer-events-none absolute inset-0" data-id="element-1321" data-mp="1c1bggb7mny9v">
      {/* Chapter mark */}
      <motion.p {...item(0)} className="absolute left-6 top-24 text-[13px] font-light tracking-[0.04em] text-paper/80 md:left-16 md:top-28" data-id="element-1322" data-mp="1h0o4gza8jbe1">
        {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')} — {scene.chapter}
      </motion.p>

      {/* Large time stamp, set far back */}
      <motion.div style={{
      x: timeX,
      y: timeY
    }} className="absolute right-6 top-[18%] md:right-16" aria-hidden="true" data-id="element-1323" data-mp="1uvyx0610bkf8">
        <motion.span {...item(0.05)} className="block font-editorial text-[96px] leading-none tracking-[-0.03em] text-paper/25 md:text-[220px]" data-id="element-1324" data-mp="k203xjspmcs">
          {scene.time}
        </motion.span>
      </motion.div>

      {/* Headline and line */}
      <motion.div style={{
      x: headX,
      y: headY
    }} className="absolute bottom-24 left-6 right-6 md:bottom-28 md:left-16 md:right-auto md:max-w-[820px]" data-id="element-1325" data-mp="yxfyp18k7jip">
        <motion.p {...item(0.12)} className="text-[14px] font-light text-paper/75" data-id="element-1326" data-mp="17wt0d7qfz3sv">
          {scene.place}
        </motion.p>
        <motion.h2 {...item(0.2)} className="mt-4 font-editorial text-[42px] leading-[46px] tracking-[-0.015em] [text-shadow:0_2px_40px_rgba(0,0,0,0.5)] md:text-[78px] md:leading-[80px]" data-id="element-1327" data-mp="1mta8lq3c42b7">
          {scene.title}
        </motion.h2>
        <motion.p {...item(0.32)} className="mt-5 max-w-[520px] text-[17px] font-light leading-[27px] text-paper/85 md:text-[20px] md:leading-[30px]" data-id="element-1328" data-mp="1vo85f89lrz7t">
          {scene.line}
        </motion.p>
        {scene.details && <motion.dl {...item(0.44)} className="mt-8 flex flex-wrap gap-x-10 gap-y-3 border-t border-paper/20 pt-5" data-id="element-1329" data-mp="20m09f0nivbsd">
            {scene.details.map(d => <div key={d.label} data-id="element-1330" data-mp="2k5771jilmp24">
                <dt className="text-[12px] font-light text-paper/60" data-id="element-1331" data-mp="2p63do4uc4pb8">{d.label}</dt>
                <dd className="mt-1 text-[15px]" data-id="element-1332" data-mp="2a9480mj1n6je">{d.value}</dd>
              </div>)}
          </motion.dl>}
      </motion.div>

      {/* Status chips, closest to the viewer */}
      <motion.ul style={{
      x: chipX,
      y: chipY
    }} className="absolute bottom-28 right-16 hidden flex-col items-end gap-3 lg:flex" data-id="element-1333" data-mp="2fa06ulojg2hy">
        {scene.chips.map((chip, c) => <motion.li key={chip} {...item(0.4 + c * 0.08)} className="inline-flex items-center gap-2.5 border border-paper/30 bg-ink/30 px-4 py-2.5 text-[14px] font-light text-paper backdrop-blur-md" data-id="element-1334" data-mp="268tqcclw2rop" data-mpi={c}>
            <span className="h-1.5 w-1.5 rounded-full bg-[#f1dfbf]" aria-hidden="true" data-id="element-1335" data-mp="1ihejc6j63m74" />
            {chip}
          </motion.li>)}
      </motion.ul>
    </div>;
}